// src/ui/Feedback.tsx (T1): loading and status parts. Spinner (a polite status), Skeleton blocks for loading
// screens (spec 2.9: every data screen has a skeleton), Banner (demo, verify email, team mode), VisuallyHidden.
// Root classes: hb-spinner, hb-skeleton, hb-banner.
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { cx } from "./cx";
import { Icon, type IconName } from "./Icon";

export function Spinner(props: { label?: string; inline?: boolean; class?: string }) {
  return (
    <div class={cx("hb-spinner", props.inline && "hb-spinner-inline", props.class)} role="status" aria-live="polite">
      <span class="hb-spinner-ring" aria-hidden="true" />
      <span class="sr">{props.label ?? "Loading"}</span>
    </div>
  );
}

/** How long a page may take, counted from when it opened, before it shows a spinner. */
export const QUIET_MS = 600;

/**
 * The spinner of a page that is starting (the app's start, a screen's code, a sign-in link being used): nothing until about
 * 600ms have passed since the page opened (performance.now() counts from then). A quick start shows plain white and then the
 * screen; a slow one shows one spinner in one place, carried from step to step rather than blinking off and on.
 */
export function QuietSpinner(props: { label?: string }) {
  const [late, setLate] = useState(() => performance.now() >= QUIET_MS);
  useEffect(() => {
    if (late) return;
    const timer = setTimeout(() => setLate(true), Math.max(0, QUIET_MS - performance.now()));
    return () => clearTimeout(timer);
  }, []);
  return late ? <Spinner label={props.label} /> : null;
}

/** A grey placeholder block. `lines` renders that many text lines; otherwise give it a class with a size. */
export function Skeleton(props: { lines?: number; width?: string; height?: string; ratio?: string; class?: string }) {
  if (props.lines) {
    return (
      <div class={cx("hb-skeleton-lines", props.class)} aria-hidden="true">
        {Array.from({ length: props.lines }, (_, i) => (
          <span key={i} class="hb-skeleton" style={{ width: i === props.lines! - 1 ? "62%" : "100%" }} />
        ))}
      </div>
    );
  }
  const style: Record<string, string> = {};
  if (props.width) style.width = props.width;
  if (props.height) style.height = props.height;
  if (props.ratio) style.aspectRatio = props.ratio;
  return <span class={cx("hb-skeleton", "hb-skeleton-block", props.class)} style={style} aria-hidden="true" />;
}

/**
 * A full-width strip at the top of the page: `quiet` (the demo strip), `info` (verify your email, team mode).
 * One short sentence and at most one action.
 */
export function Banner(props: { tone?: "quiet" | "info"; icon?: IconName; action?: ComponentChildren; class?: string; children?: ComponentChildren }) {
  const tone = props.tone ?? "info";
  return (
    <div class={cx("hb-banner", `hb-banner-${tone}`, props.class)} role={tone === "quiet" ? "note" : "status"}>
      <div class="hb-banner-in">
        {props.icon ? <Icon name={props.icon} size="sm" /> : null}
        <span class="hb-banner-t">{props.children}</span>
        {props.action ? <span class="hb-banner-act">{props.action}</span> : null}
      </div>
    </div>
  );
}

export function VisuallyHidden(props: { as?: "span" | "p" | "h1" | "h2"; children?: ComponentChildren }) {
  const Tag = props.as ?? "span";
  return <Tag class="sr">{props.children}</Tag>;
}
